
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EquipmentType {
  id: string;
  name: string;
  base_price: number;
}

interface EquipmentItem {
  equipmentTypeId: string;
  equipmentTypeName: string;
  basePrice: number;
  quantity: number;
  isSelected: boolean;
}

interface Team {
  id: string;
  name: string;
}

interface EquipmentInventoryItem {
  id: string;
  name: string;
  base_price: number;
}

// Define schema outside the component to avoid recursive type issues
const assignmentSchema = z.object({
  teamId: z.string().min(1, "Please select a team"),
  notes: z.string().optional(),
  equipmentItems: z.array(
    z.object({
      equipmentTypeId: z.string(),
      equipmentTypeName: z.string(),
      basePrice: z.number(),
      quantity: z.number().int().positive("Quantity must be positive"),
      isSelected: z.boolean(),
    })
  ).refine(
    (items) => items.some(item => item.isSelected),
    {
      message: "Select at least one equipment item",
    }
  ),
});

// Define form values type
type AssignmentFormValues = z.infer<typeof assignmentSchema>;

export const TeamEquipmentAssignmentTab = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingEquipment, setExistingEquipment] = useState<EquipmentInventoryItem[]>([]);
  const [assignedEquipment, setAssignedEquipment] = useState<{name: string; quantity: number; basePrice: number}[]>([]);
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  const { user } = useSupabaseUser();
  
  const form = useForm<AssignmentFormValues>({
    resolver: zodResolver(assignmentSchema),
    defaultValues: {
      teamId: "",
      notes: "",
      equipmentItems: [],
    },
  });
  
  useEffect(() => {
    fetchTeams();
    fetchEquipmentTypes();
    fetchExistingEquipment();
  }, []);
  
  const fetchTeams = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .eq('status', 'active')
        .order('name');
        
      if (error) throw error;
      
      setTeams(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load teams: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchExistingEquipment = async () => {
    try {
      const { data, error } = await supabase
        .from('equipment_types')
        .select('id, name, base_price');
        
      if (error) throw error;
      
      setExistingEquipment(data || []);
      console.log("Fetched existing equipment:", data);
    } catch (error: any) {
      console.error("Error fetching existing equipment:", error);
      toast({
        title: "Error",
        description: `Failed to load equipment inventory: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  const fetchEquipmentTypes = async () => {
    try {
      setIsLoading(true);
      
      // Force fresh data by adding timestamp to prevent caching
      const { data, error } = await supabase
        .from('team_equipment_types')
        .select('id, name, base_price, admin_id')
        .order('name');
        
      if (error) {
        console.error('Error fetching team equipment types:', error);
        throw error;
      }
      
      console.log('Fetched team equipment types:', data); // Debug log
      console.log('Current user ID:', user?.id); // Debug log
      
      // Set up form values for equipment types
      const equipmentItems: EquipmentItem[] = (data || []).map(type => ({
        equipmentTypeId: type.id,
        equipmentTypeName: type.name,
        basePrice: type.base_price,
        quantity: 1,
        isSelected: false,
      }));
      
      form.setValue('equipmentItems', equipmentItems);
      
      setEquipmentTypes(data || []);
    } catch (error: any) {
      console.error('Failed to load equipment types:', error);
      toast({
        title: "Error",
        description: `Failed to load equipment types: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignedEquipment = async (teamId: string) => {
    try {
      const { data, error } = await supabase
        .from('team_equipment')
        .select(`
          quantity,
          team_equipment_types!team_equipment_equipment_type_id_fkey (
            name,
            base_price
          )
        `)
        .eq('team_id', teamId)
        .is('returned_at', null);

      if (error) throw error;

      const formatted = (data || []).map((item: any) => ({
        name: item.team_equipment_types?.name || 'Unknown',
        quantity: item.quantity,
        basePrice: item.team_equipment_types?.base_price || 0
      }));

      setAssignedEquipment(formatted);
    } catch (error: any) {
      console.error('Error fetching assigned equipment:', error);
      setAssignedEquipment([]);
    }
  };

  const handleTeamChange = (teamId: string) => {
    form.setValue('teamId', teamId);
    if (teamId) {
      fetchAssignedEquipment(teamId);
    } else {
      setAssignedEquipment([]);
    }
  };
  
  const onSubmit = async (values: AssignmentFormValues) => {
    try {
      setIsSubmitting(true);
      
      if (!user?.id) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to assign equipment",
          variant: "destructive",
        });
        return;
      }
      
      const selectedEquipment = values.equipmentItems.filter(item => item.isSelected);
      
      for (const item of selectedEquipment) {
        console.log(`Processing equipment type: ${item.equipmentTypeId} - ${item.equipmentTypeName}`);
        
        // We need to make sure we're using team_equipment_types IDs
        // First check if this equipment type exists in the team_equipment_types table
        const { data: teamEquipmentType, error: teamEquipmentError } = await supabase
          .from('team_equipment_types')
          .select('id')
          .eq('id', item.equipmentTypeId)
          .maybeSingle();
        
        if (teamEquipmentError) {
          console.error("Error checking team equipment type:", teamEquipmentError);
          throw teamEquipmentError;
        }
        
        if (!teamEquipmentType) {
          // The equipment type doesn't exist in team_equipment_types
          toast({
            title: "Error",
            description: `${item.equipmentTypeName} is not available for team assignment. Please select a valid team equipment type.`,
            variant: "destructive",
          });
          continue; // Skip this item and proceed with others
        }
        
        // Now check if this equipment is already assigned to the team
        const { data: existingAssignment, error: checkError } = await supabase
          .from('team_equipment')
          .select('id, quantity, notes')
          .eq('team_id', values.teamId)
          .eq('equipment_id', item.equipmentTypeId)
          .is('returned_at', null)
          .maybeSingle();
        
        if (checkError) {
          console.error("Error checking existing equipment assignment:", checkError);
          throw checkError;
        }
        
        if (existingAssignment) {
          // Update the existing assignment by adding to quantity
          console.log(`Updating existing equipment assignment for team ${values.teamId} with quantity ${item.quantity + existingAssignment.quantity}`);
          const { error: updateError } = await supabase
            .from('team_equipment')
            .update({ 
              quantity: item.quantity + existingAssignment.quantity,
              notes: values.notes || existingAssignment.notes,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingAssignment.id);
            
          if (updateError) {
            console.error("Error updating equipment assignment:", updateError);
            throw updateError;
          }
          
          console.log("Equipment assignment updated successfully");
        } else {
          // Assign new equipment to team
          console.log(`Assigning equipment ${item.equipmentTypeId} to team ${values.teamId} with quantity ${item.quantity}`);
          const { error: assignmentError } = await supabase
            .from('team_equipment')
            .insert({
              team_id: values.teamId,
              equipment_type_id: item.equipmentTypeId,
              quantity: item.quantity,
              notes: values.notes || null,
              assigned_by: user.id,
            });
            
          if (assignmentError) {
            console.error("Error assigning equipment to team:", assignmentError);
            throw assignmentError;
          }
          
          console.log("Equipment assigned successfully");
        }
      }
      
      // Refresh equipment data
      await fetchExistingEquipment();
      
      toast({
        title: "Equipment assigned",
        description: "Equipment has been successfully assigned to the team",
      });
      
      // Reset the form
      form.reset({
        teamId: "",
        notes: "",
        equipmentItems: equipmentTypes.map(type => ({
          equipmentTypeId: type.id,
          equipmentTypeName: type.name,
          basePrice: type.base_price,
          quantity: 1,
          isSelected: false,
        })),
      });
    } catch (error: any) {
      console.error("Complete error in onSubmit:", error);
      toast({
        title: "Error",
        description: `Failed to assign equipment: ${error.message || "Unknown error occurred"}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const toggleEquipmentSelection = (index: number) => {
    const currentItems = form.getValues('equipmentItems');
    currentItems[index].isSelected = !currentItems[index].isSelected;
    form.setValue('equipmentItems', [...currentItems]);
  };

  const selectedItemsCount = form.watch('equipmentItems')?.filter(item => item.isSelected)?.length || 0;
  
  if (!isAdmin) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Only administrators can manage team equipment assignments.
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Assign Equipment to Teams</h2>
      
      {equipmentTypes.length === 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-amber-800">
              No team equipment types found. Please add team equipment types first.
            </p>
          </CardContent>
        </Card>
      )}
      
      {isLoading ? (
        <div className="p-4 text-center">Loading...</div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="teamId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Team</FormLabel>
                  <Select onValueChange={handleTeamChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a team" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Show currently assigned equipment */}
            {form.watch('teamId') && assignedEquipment.length > 0 && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 text-green-900">Χρεωμένο στην ομάδα:</h3>
                  <div className="space-y-2">
                    {assignedEquipment.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="font-medium">{item.name}</span>
                        <span className="text-green-700">Ποσότητα: {item.quantity}</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-green-300 mt-2">
                      <div className="flex justify-between font-semibold text-green-900">
                        <span>Σύνολο Εξοπλισμού:</span>
                        <span>{assignedEquipment.reduce((sum, item) => sum + item.quantity, 0)} τεμάχια</span>
                      </div>
                      <div className="flex justify-between font-semibold text-green-900 mt-1">
                        <span>Συνολική Αξία:</span>
                        <span>€{assignedEquipment.reduce((sum, item) => sum + (item.quantity * item.basePrice), 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <div>
              <FormLabel className="block mb-2">Select Equipment Types</FormLabel>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    Select Equipment Types
                    {selectedItemsCount > 0 && (
                      <span className="ml-2 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-xs">
                        {selectedItemsCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full md:w-[400px]" align="start">
                  <DropdownMenuLabel>Available Equipment Types</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {equipmentTypes.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">No equipment types available</div>
                  ) : (
                    <div className="max-h-[300px] overflow-auto">
                      {equipmentTypes.map((type, index) => {
                        const isSelected = form.watch(`equipmentItems.${index}.isSelected`);
                        
                        return (
                          <DropdownMenuCheckboxItem
                            key={type.id}
                            checked={isSelected}
                            onCheckedChange={() => toggleEquipmentSelection(index)}
                          >
                            <div className="flex flex-col">
                              <span>{type.name}</span>
                              <span className="text-xs text-muted-foreground">
                                Base Price: €{type.base_price.toFixed(2)}
                              </span>
                            </div>
                          </DropdownMenuCheckboxItem>
                        );
                      })}
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              
              {form.formState.errors.equipmentItems?.message && (
                <p className="text-sm text-destructive mt-2">{form.formState.errors.equipmentItems.message}</p>
              )}
              
              {/* Display selected items with quantity controls */}
              {selectedItemsCount > 0 && (
                <Card className="mt-4">
                  <CardContent className="p-4">
                    <h3 className="font-medium mb-2">Selected Equipment</h3>
                    <div className="space-y-3">
                      {form.watch('equipmentItems')
                        .filter(item => item.isSelected)
                        .map((item, originalIndex) => {
                          // Find the original index in the full array
                          const index = form.getValues('equipmentItems').findIndex(
                            i => i.equipmentTypeId === item.equipmentTypeId
                          );
                          
                          // Default max quantity
                          const maxQuantity = 100;
                          
                          return (
                            <div key={item.equipmentTypeId} className="flex items-center space-x-4 py-2 border-b last:border-b-0">
                              <div className="flex-grow">
                                <p className="font-medium">{item.equipmentTypeName}</p>
                                <p className="text-sm text-muted-foreground">
                                  Base Price: €{item.basePrice.toFixed(2)}
                                </p>
                              </div>
                              <FormField
                                control={form.control}
                                name={`equipmentItems.${index}.quantity`}
                                render={({ field }) => (
                                  <FormItem className="w-20 flex-shrink-0">
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="1"
                                        max={maxQuantity}
                                        {...field}
                                        onChange={(e) => {
                                          const value = parseInt(e.target.value) || 1;
                                          // Ensure value doesn't exceed available quantity
                                          const sanitizedValue = Math.min(value, maxQuantity);
                                          form.setValue(
                                            `equipmentItems.${index}.quantity`, 
                                            sanitizedValue
                                          );
                                        }}
                                        className="w-20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => toggleEquipmentSelection(index)}
                              >
                                &times;
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add any notes about this assignment" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={form.getValues('teamId') === "" || selectedItemsCount === 0 || equipmentTypes.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning Equipment...
                </>
              ) : (
                "Assign Equipment to Team"
              )}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
};
